import React from "react";
import { commitMutation, graphql } from "react-relay";

import { faScrewdriverWrench } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { Intune } from "@fellow/intune";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as Sentry from "@sentry/browser";
import cx from "classnames";

import AppContext from "~/common/AppContext";
import ThemeContext from "~/common/ThemeContext";
import { useLazyLoadQuery } from "~/lib/relayUtil";
import { withSuspense } from "~/lib/suspense";
import { getFirstUserFacingError } from "~/lib/utils";
import WebsocketClient from "~/lib/websocket";
import { environment } from "~/models";
import { Checkbox, toast } from "~/ui";
import { Button } from "~/ui/button";
import { Input, Modal } from "~/ui/legacyFellowIndex";
import Menu from "~/ui/menu";
import { useModals } from "~/ui/modals";
import { useUXSimplificationFeature } from "~/uxSimplificationFeature";
import { useTourContext } from "~/welcomeChecklist";

import useIsMicrophoneRecordingEnabled, {
	testCreateAndUploadClientSideRecording,
} from "../meetingRecorder/RecordingControls/MicrophoneRecording";
import { LoadingFallback } from "../meetingRecorder/TranscriptModal";

import type {
	DevMenuAutoRecordEventDataQuery,
	DevMenuAutoRecordEventDataQuery$data,
} from "~/__generated__/DevMenuAutoRecordEventDataQuery.graphql";
import type { DevMenuRecordingListDetailsModalQuery as RecordingListDetailsModalQueryType } from "~/__generated__/DevMenuRecordingListDetailsModalQuery.graphql";
import type { DevMenuSmartTrackerProcessingMutation } from "~/__generated__/DevMenuSmartTrackerProcessingMutation.graphql";
import type { DevMenuTrackerProcessingQuery } from "~/__generated__/DevMenuTrackerProcessingQuery.graphql";

import securityStyles from "~/account/Security/css/SecurityAuditLog.module.css";
import accountPanelStyles from "~/nav/panels/Account/AccountPanel.module.css";

import styles from "./DevMenu.module.css";

const datetimeFormatter = new Intl.DateTimeFormat("en-US", {
	month: "short",
	year: "numeric",
	day: "numeric",
	hour: "numeric",
	minute: "2-digit",
});

const AutoRecordEventDataQuery = graphql`
	query DevMenuAutoRecordEventDataQuery {
		viewer {
			devMenuAutoRecordEventData {
				rule {
					ruleType
					ruleLevel
					filterAttendeeType
					filterMinimumAttendees
					filterMaximumAttendees
					filterKeywordsContains
					filterKeywordsRestrict
					autoRecordRuleUsers {
						includeAllReports
						user {
							id
							fullName
							email
						}
					}
				}
				record
				event {
					title
					start
				}
				user {
					fullName
				}
			}
		}
	}
`;

const RecordingListDetailsModalQuery = graphql`
	query DevMenuRecordingListDetailsModalQuery {
		devMenuVideoRecordingsAndClips {
			dbId
			createdAt
			title
			type
			dueForConversionAt
		}
	}
`;

const TrackerProcessingQuery = graphql`
	query DevMenuTrackerProcessingQuery {
		viewer {
			account {
				smartTrackers {
					id
					name
					isActive
				}
			}
		}
	}
`;

function WebsocketDebug({ close }: { close: () => void }) {
	const wsClient = WebsocketClient.getInstance();
	return (
		<Modal onClose={close} width="45.25rem">
			<Modal.Header onClose={close}>
				<h3>WS Debug Info</h3>
			</Modal.Header>
			<Modal.Body>
				<div className={styles.experiments}>
					{wsClient.socket ? (
						<pre>{JSON.stringify(wsClient.socket.dumpDebugState(), null, "  ")}</pre>
					) : (
						"WS Client not yet initialized"
					)}
				</div>
			</Modal.Body>
		</Modal>
	);
}

function AutoRecordEventDataInstance({
	instance,
}: {
	instance: DevMenuAutoRecordEventDataQuery$data["viewer"]["devMenuAutoRecordEventData"][0];
}) {
	const title = instance?.event.title;
	const start = React.useMemo(() => {
		if (instance?.event.start) {
			let date = new Date(instance?.event.start);
			return `${date.toLocaleDateString()} at ${date.toLocaleTimeString()}`;
		} else {
			return null;
		}
	}, [instance?.event.start]);
	const rule = instance?.rule;
	const record = instance?.record ? true : false;

	const user = instance?.user?.fullName;

	const [showRuleInfo, setShowRuleInfo] = React.useState(false);

	const toggleShwoRule = React.useCallback(() => {
		setShowRuleInfo(!showRuleInfo);
	}, [showRuleInfo]);

	if (instance === null) {
		return <>oops!</>;
	}

	return (
		<div className={styles.autoRecordEventDataInstance}>
			<span>
				Title: <b>{title}</b> on <b>{start}</b>
			</span>
			<span>
				Should record: <b>{record.toString()}</b>
			</span>
			<span>
				Recording rule applied to decision: <b>{rule ? "Yes" : "No"}</b>
			</span>
			{user && (
				<span>
					Rule applied by (will show as bot invitee): <b>{user}</b>
				</span>
			)}
			{rule && (
				<>
					<Button variant="secondary" size="slim" onClick={toggleShwoRule}>
						Rule info
					</Button>
					{showRuleInfo && (
						<>
							<div>
								<span>{JSON.stringify(rule, null, 2)}</span>
							</div>
						</>
					)}
				</>
			)}
		</div>
	);
}

function AutoRecordEventDataForUser({ close }: { close: () => void }) {
	const { viewer } = useLazyLoadQuery<DevMenuAutoRecordEventDataQuery>(
		AutoRecordEventDataQuery,
		{},
		{ fetchPolicy: "store-and-network" },
	);

	const autoRecordEventDataForWeek: DevMenuAutoRecordEventDataQuery$data["viewer"]["devMenuAutoRecordEventData"] =
		viewer.devMenuAutoRecordEventData;

	return (
		<Modal onClose={close} width="45.25rem">
			<Modal.Header onClose={close}>
				<h3>Test Auto Record Rules for week</h3>
			</Modal.Header>
			<Modal.Body>
				<div className={styles.autoRecordEventDataContainer}>
					{autoRecordEventDataForWeek &&
						autoRecordEventDataForWeek.map(instance => (
							<AutoRecordEventDataInstance instance={instance} key={instance?.event.start} />
						))}
				</div>
			</Modal.Body>
		</Modal>
	);
}

function RecordingListDetailsModal({ close }: { close: () => void }) {
	const { devMenuVideoRecordingsAndClips: data } = useLazyLoadQuery<RecordingListDetailsModalQueryType>(
		RecordingListDetailsModalQuery,
		{},
		{ fetchPolicy: "store-and-network" },
	);

	return (
		<Modal onClose={close} width="45.25rem">
			<Modal.Header onClose={close}>Recording & clip list details</Modal.Header>
			<Modal.Body>
				<table className={securityStyles.dataContainer}>
					<thead className={securityStyles.header}>
						<tr>
							<th className={securityStyles.title}>ID</th>
							<th className={securityStyles.title}>Title</th>
							<th className={securityStyles.title}>Type</th>
							<th className={securityStyles.title}>Created at</th>
							<th className={securityStyles.title}>Due for conversion at</th>
						</tr>
					</thead>

					<tbody className={securityStyles.tableBody}>
						{data.map(instance => (
							<tr key={`${instance.type}-${instance.dbId}`}>
								<td className={cx(securityStyles.data, securityStyles.item)}>{instance.dbId}</td>
								<td className={cx(securityStyles.data, securityStyles.item, styles.textEllipsis, styles.videoTitle)}>
									{instance.title}
								</td>
								<td className={cx(securityStyles.data, securityStyles.item)}>{instance.type}</td>
								<td className={cx(securityStyles.data, securityStyles.item)}>
									{datetimeFormatter.format(new Date(instance.createdAt))}
								</td>
								<td className={cx(securityStyles.data, securityStyles.item)}>
									{instance.dueForConversionAt
										? datetimeFormatter.format(new Date(instance.dueForConversionAt))
										: "None"}
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</Modal.Body>
		</Modal>
	);
}

const RecordingListDetailsModalWithSuspense = withSuspense(<LoadingFallback />, RecordingListDetailsModal);

function SmartTrackerProcessingModal({ close }: { close: () => void }) {
	const { viewer } = useLazyLoadQuery<DevMenuTrackerProcessingQuery>(
		TrackerProcessingQuery,
		{},
		{ fetchPolicy: "store-and-network" },
	);

	const smartTrackers = viewer.account?.smartTrackers;

	const [selectedTrackers, setSelectedTrackers] = React.useState<string[]>([]);
	const toggleTracker = React.useCallback(
		(trackerId: string) => () => {
			setSelectedTrackers(prev => {
				if (prev.includes(trackerId)) {
					return prev.filter(id => id !== trackerId);
				} else {
					return [...prev, trackerId];
				}
			});
		},
		[],
	);

	const [eventGuid, setEventGuid] = React.useState("");
	const onEventGuidChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		setEventGuid(e.target.value);
	}, []);

	const processSmartTrackers = React.useCallback(() => {
		commitMutation<DevMenuSmartTrackerProcessingMutation>(environment, {
			mutation: graphql`
				mutation DevMenuSmartTrackerProcessingMutation($eventGuid: String!, $smartTrackerIds: [ID!]!) {
					devMenuProcessSmartTrackers(eventGuid: $eventGuid, smartTrackerIds: $smartTrackerIds) {
						ok
					}
				}
			`,
			variables: {
				eventGuid,
				smartTrackerIds: selectedTrackers,
			},
			onCompleted: (response, errors) => {
				if (errors || !response || !response.devMenuProcessSmartTrackers) {
					const userFacingError = getFirstUserFacingError(errors);
					if (userFacingError && errors && errors[0].message === "MEETING_RECORDING_NOT_FOUND") {
						toast.error("Meeting recording not found");
						return;
					}
					toast.error("Error trying to process trackers");
					return;
				}

				if (response.devMenuProcessSmartTrackers.ok) {
					toast.success("Smart trackers processing started successfully");
					close();
				}
			},
			onError: error => {
				console.error(error);
			},
		});
	}, [close, eventGuid, selectedTrackers]);

	return (
		<Modal onClose={close}>
			<Modal.Header onClose={close}>
				<Modal.Title>Process Smart Trackers</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				<div className={styles.sectionContent}>
					<label className={styles.inputLabel}>Event GUID</label>
					<Input
						className={styles.input}
						autoFocus
						type="text"
						placeholder="ssp75pf6vevejt2v8njdbutvul_20241217T190000Z"
						value={eventGuid}
						onChange={onEventGuidChange}
						name="dev_event_guid"
					/>
				</div>
				<h2 className={styles.sectionTitle}>Select trackers to process</h2>
				<div className={styles.sectionContent}>
					<label className={styles.inputLabel}>Active Trackers</label>
					{smartTrackers &&
						smartTrackers
							.filter(tracker => tracker.isActive)
							.map(tracker => (
								<div key={tracker.id} className={styles.smartTracker}>
									<Checkbox
										checked={selectedTrackers.includes(tracker.id)}
										onCheckedChange={toggleTracker(tracker.id)}
									/>
									<span>{tracker.name}</span>
								</div>
							))}
					<label className={styles.inputLabel}>Inactive Trackers</label>
					{smartTrackers &&
						smartTrackers
							.filter(tracker => !tracker.isActive)
							.map(tracker => (
								<div key={tracker.id} className={styles.smartTracker}>
									<Checkbox
										checked={selectedTrackers.includes(tracker.id)}
										onCheckedChange={toggleTracker(tracker.id)}
									/>
									<span>{tracker.name}</span>
								</div>
							))}
				</div>
			</Modal.Body>
			<Modal.Footer>
				<Button
					variant="primary"
					disabled={eventGuid === "" || selectedTrackers.length === 0}
					onClick={processSmartTrackers}
				>
					Process smart trackers
				</Button>
			</Modal.Footer>
		</Modal>
	);
}

function DevMenu({ inPanel, toggleMockIntercomBubble }: { inPanel?: boolean; toggleMockIntercomBubble?: () => void }) {
	const [bannerText, setBannerText] = React.useState(window.ENVIRONMENT.replace("sandbox", "sb"));

	const measureReflow = React.useCallback(() => {
		if (!("getEntriesByName" in performance)) {
			window.alert("performance.getEntriesByName not available - are you using Opera?");
			return;
		}
		const rootEl = document.documentElement;
		const initialFontSize = rootEl.style.fontSize;
		window.setTimeout(() => {
			performance.mark("reflow-start");

			// Invalidating the whole page's layout
			rootEl.style.fontSize = "15px";
			// Accessing this value causes the reflow to happen synchronously, now
			console.debug(window.getComputedStyle(rootEl).height);

			// Combining rAF and setTimeout lets us execute this as soon as the following frame has rendered,
			// i.e. after the reflow has finished. More details:
			// https://nolanlawson.com/2018/09/25/accurately-measuring-layout-on-the-web/
			window.requestAnimationFrame(() => {
				window.setTimeout(() => {
					performance.mark("reflow-end");
					performance.measure("reflow time", "reflow-start", "reflow-end");
					// eslint-disable-next-line compat/compat
					const [measurement] = performance.getEntriesByName("reflow time", "measure");
					setBannerText(`${measurement.duration.toFixed(0)}ms`);

					performance.clearMarks("reflow-start");
					performance.clearMarks("reflow-end");
					performance.clearMeasures("reflow time");
					rootEl.style.fontSize = initialFontSize;
				}, 0);
			});
		}, 10);
	}, []);

	const modals = useModals();

	const openWSDebug = React.useCallback(() => {
		modals?.open(({ close }) => <WebsocketDebug close={close} />);
	}, [modals]);

	const { isDarkMode, setTheme } = React.useContext(ThemeContext);
	const toggleTheme = React.useCallback(() => {
		setTheme(isDarkMode ? "LIGHT" : "DARK");
	}, [isDarkMode, setTheme]);

	// Whenever `bannerText` changes, reset it after 5000ms
	React.useEffect(() => {
		const timeout = window.setTimeout(() => setBannerText(window.ENVIRONMENT.replace("sandbox", "sb")), 5000);
		return () => window.clearTimeout(timeout);
	}, [bannerText]);

	const { user } = React.useContext(AppContext);

	const openIntuneConsole = React.useCallback(() => {
		Intune.openIntuneConsole().catch(Sentry.captureException);
	}, []);

	const checkEnrolledUser = React.useCallback(async () => {
		const { account } = await Intune.getEnrolledAccount();
		// eslint-disable-next-line no-restricted-globals
		alert(JSON.stringify(account, null, 2));
	}, []);

	const unenroll = React.useCallback(() => {
		if (!user?.email) {
			throw new Error("No user");
		}
		Intune.deRegisterAndUnenrollAccount({ email: user.email, withWipe: true }).catch(Sentry.captureException);
	}, [user?.email]);

	const openAutoRecordEventDataModal = React.useCallback(() => {
		modals?.open(({ close }) => <AutoRecordEventDataForUser close={close} />);
	}, [modals]);

	const openRecordingListDetailsModal = React.useCallback(() => {
		modals?.open(({ close }) => <RecordingListDetailsModalWithSuspense close={close} />);
	}, [modals]);

	const { dropConfetti, isTestingMode, toggleTestingMode } = useTourContext();

	const isMicrophoneRecordingEnabled = useIsMicrophoneRecordingEnabled();

	const openSmartTrackerProcessingModal = React.useCallback(() => {
		modals?.open(({ close }) => <SmartTrackerProcessingModal close={close} />);
	}, [modals]);

	const simulateClientSideRecording = React.useCallback(() => {
		const input = document.createElement("input");
		input.type = "file";
		input.accept = "audio/mp4";
		input.onchange = e => {
			const file = (e.target as HTMLInputElement).files?.[0];
			if (file) {
				testCreateAndUploadClientSideRecording(file);
			}
		};
		input.click();
	}, []);

	const { isUXSimplificationFeatureFlagEnabled, toggleUXSimplification } = useUXSimplificationFeature();
	const toggleUXSimplificationWithoutRedirect = React.useCallback(
		(enabled: boolean) => {
			toggleUXSimplification(enabled, { skipRedirect: true });
		},
		[toggleUXSimplification],
	);

	return (
		<Menu mobileHeaderTitle="Dev Menu">
			<Menu.Trigger asChild>
				{inPanel ? (
					<button className={cx(accountPanelStyles.overflowLink, styles.devMenuInPanel)}>
						<FontAwesomeIcon icon={faScrewdriverWrench} className={accountPanelStyles.overflowLinkIcon} />

						<span className={cx(accountPanelStyles.overflowLinkText, styles.devMenuInPanelText)}>
							Developer tools
							<br />
							<small>{bannerText}</small>
						</span>
					</button>
				) : (
					<Button variant="accent" size="slim" className={styles.environmentBanner}>
						{bannerText}
					</Button>
				)}
			</Menu.Trigger>
			<Menu.Content>
				<Menu.Item onSelect={measureReflow}>Measure reflow time</Menu.Item>
				<Menu.Item onSelect={openWSDebug}>Show websocket debug info</Menu.Item>
				<Menu.Item onSelect={toggleTheme}>Toggle dark mode</Menu.Item>
				<Menu.Item onSelect={openSmartTrackerProcessingModal}>Process smart trackers</Menu.Item>
				<Menu.Item onSelect={toggleTestingMode}>Turn {isTestingMode ? "off" : "on"} tour testing</Menu.Item>
				<Menu.Item onSelect={dropConfetti}>Hooray!</Menu.Item>
				{toggleMockIntercomBubble && (
					<Menu.Item onSelect={toggleMockIntercomBubble}>Toggle mock Intercom bubble</Menu.Item>
				)}
				<Menu.Sub mobileHeaderTitle="Intune">
					<Menu.SubTrigger>Intune</Menu.SubTrigger>
					<Menu.SubContent>
						<Menu.Item onSelect={openIntuneConsole}>Open Console</Menu.Item>
						<Menu.Item onSelect={checkEnrolledUser}>Check Enrolled User</Menu.Item>
						<Menu.Item onSelect={unenroll}>Unenroll</Menu.Item>
					</Menu.SubContent>
				</Menu.Sub>
				<Menu.Item onSelect={openAutoRecordEventDataModal}>Test Auto Record Rules for week</Menu.Item>
				<Menu.Item onSelect={openRecordingListDetailsModal}>Show recording & clip list details</Menu.Item>
				{isMicrophoneRecordingEnabled && (
					<Menu.Item onSelect={simulateClientSideRecording}>Simulate client-side recording</Menu.Item>
				)}
				{isUXSimplificationFeatureFlagEnabled && (
					<Menu.SwitchItem
						checked={user?.uxSimplificationEnabled}
						onCheckedChange={toggleUXSimplificationWithoutRedirect}
					>
						UX Simplification
					</Menu.SwitchItem>
				)}
			</Menu.Content>
		</Menu>
	);
}

export default React.memo(DevMenu);
